import clubPlaceholder from "assets/images/logo_fixtures.png";
import clsx from "clsx";
import { useI18n } from "features/translation/context";
import {
	formatAssignedGameClubMatchup,
	formatAssignedGameId,
	formatAssignedGameTypeAndDate,
	hasAssignedGameDetail,
} from "features/videoUploads/assignedGameDisplay";
import type { Metadata } from "features/videoUploads/metadata/types";
import { _, _any } from "i18n";
import type { GameData } from "libs/apiClient";
import client from "libs/client";
import { useState } from "react";
import { Link } from "react-router";
import type { SingleValue } from "react-select";
import DateRangeCalendar from "ui-kit/src/DateRangeCalendar/DateRangeCalendar";
import Loader from "ui-kit/src/Loader/Loader";
import SearchInput from "ui-kit/src/SearchInput/SearchInput"
import { DebouncedAsyncSelect } from "ui-kit/src/SelectComponent/DebouncedAsyncSelect"

interface GameSelectComponentProps {
	metadata: Metadata | null;
	withVideo: boolean;
	onGameSelect: (game: number) => void;
}

export function GameSelectComponent({ withVideo, metadata, onGameSelect }: GameSelectComponentProps) {
	const [selectedClub, setSelectedClub] = useState<SingleValue<{ value: number | string, label: string }>>();
	const [selectedGame, setSelectedGame] = useState<GameData | undefined>();
	const [clubInputValue, setClubInputValue] = useState<string>("");
	const [dateRange, setDateRange] = useState({ start: "", end: "" })
	const [filterInput, setFilterInput] = useState("");
	const [filterValue, setFilterValue] = useState<string>();
	const { locale } = useI18n();

	const { data: clubs, isFetching: isClubsLoading } = client.getClubs.useQuery({
		filters: [{
			field: "clubName",
			type: "search",
			value: clubInputValue
		}], withVideo: withVideo ? 1 : 0
	}, { enabled: clubInputValue?.length > 2 })

	const { data: games, isFetching: isGamesLoading } = client.getGames.useQuery({
		withVideo: withVideo ? 1 : 0,
		clubId: typeof selectedClub?.value === "number" ? selectedClub.value : 0, limit: 100,
		searchDateFrom: dateRange.start !== "" ? dateRange.start : undefined,
		searchDateTo: dateRange.start !== "" ? dateRange.end : undefined,
		searchTerm: filterValue,
	},
		{ queryKey: ["metadataGamesList"], enabled: !!(selectedClub) })

	const { data: assignedGameResponse, isLoading: isAssignedGameLoading } = client.getVideoGameDetail.useQuery(
		{ id: metadata?.gameId ?? 0 },
		{ enabled: metadata?.gameId !== undefined },
	);
	const assignedGame = assignedGameResponse?.data;
	const hasLinkedGameId = metadata?.gameId !== undefined;

	const clubOptions = clubs?.data.map((club) => ({ value: club.id, label: `${club.id} - ${club.name}` }))

	const onClubInputChange = (value: string) => {
		setClubInputValue(value);
	}

	const onClubSelect = (value: SingleValue<{ value: number | string, label: string }>) => {
		setSelectedClub(value);
	}

	const handleListRefresh = () => {
		client.queryClient.invalidateQueries({
			predicate: (query) =>
				query.queryKey.includes("metadataGamesList"),
		});

	}

	const onClearFilterInput = () => {
		setFilterValue(undefined);
		setFilterInput("");
	}

	const onFilterSubmit = () => {
		setFilterValue(filterInput);
	}

	const handleDateRangeChange = (start: string, end: string) => {
		setDateRange({ start: start, end: end })
	}

	return (
		<div className="Assignvideo-container">
			{hasLinkedGameId ? (
				hasAssignedGameDetail(assignedGame) ? (
					<Link target="_blank" to={`/admin/videos/match/${assignedGame.id}`}>
						<div className="Assignvideo-heading">
							{_("admin.video.assignedGame")}
						</div>
						<div className="Gamelisting-container">
							<div className="Assigamethumb selected">
								<div className="idCategory-container">
									{assignedGame.category != null && assignedGame.category !== "" ? <div className="category-container">{assignedGame.category}</div> : null}
									<div className="gameId-container">
										{formatAssignedGameId(assignedGame.id)}
									</div>
								</div>
								<div className="clubName">{formatAssignedGameClubMatchup(assignedGame.homeClubName, assignedGame.awayClubName)}</div>
								<div className="gameBottom">
									<div className="team-logo">
										<img src={assignedGame.homeTeamLogo ?? clubPlaceholder} alt="logo" />
									</div>
									<div className="game-score">{assignedGame.homeTeamsScore != null && assignedGame.awayTeamsScore != null ? `${assignedGame.homeTeamsScore}:${assignedGame.awayTeamsScore}` : "-"}</div>
									<div className="team-logo">
										<img src={assignedGame.awayTeamLogo ?? clubPlaceholder} alt="logo" />
									</div>
									<div className="game-details">
										{`${assignedGame.homeTeamName ?? "n/a"} - ${assignedGame.awayTeamName ?? "n/a"}`}
										<span>{formatAssignedGameTypeAndDate(assignedGame.type, assignedGame.date)} </span>
									</div>
								</div>
							</div>
						</div>
					</Link>
				) : (
					<>
						<div className="Assignvideo-heading">
							{_("admin.video.assignedGame")}
						</div>
						<div className="Gamelisting-container">
							{isAssignedGameLoading ? (
								<Loader />
							) : (
								<div className="noGamesFound" role="alert">
									{_("admin.video.info.gameUnavailable")}
								</div>
							)}
						</div>
					</>
				)
			) : null}
			<div className="Assignvideo-heading">
				<h2> {_("admin.video.assignVideoToGame")} </h2>
				<span className="icon-new icon-admin-syc icon-blue icon-24" onClick={handleListRefresh} />
			</div>
			<div className="Gamelisting-container">
				<div className="gameListing-filters">
					<div> {_("placeholder.dropdown.club")} </div>
					<div className="filterItem">
						<DebouncedAsyncSelect isLoading={isClubsLoading} options={clubOptions} onInputChange={onClubInputChange} onChange={onClubSelect} />
					</div>
					{selectedClub &&
						<>
							<div className="filterItem">
								<div> {_("admin.video.filterByDate")} </div>
								<DateRangeCalendar locale={locale} start={dateRange.start} end={dateRange.end} onChange={handleDateRangeChange} />
							</div>
							<div className="filterItem">
								<div> {_("admin.video.propertyFilter")} </div>
								<SearchInput placeholder="" value={filterInput} onChange={setFilterInput} clear={onClearFilterInput} onSubmit={onFilterSubmit} />
							</div>
						</>}
					{games?.data && games.data.length > 50 && <div className="gameFilter-warning">{_("admin.video.displayingLimitedResults")}</div>}
				</div>
				<div className="AssigameList-Container">
					{games?.data.length === 0 && selectedClub && <div className="noGamesFound">{_("error.message.no_games_found")}</div>}
					{!selectedClub && <div className="noGamesFound">{_("admin.video.selectAClub")}</div>}
					{isGamesLoading ? <Loader /> : games?.data.map((game) => {
						return (
							<div
								key={game.id}
								className={clsx("Assigamethumb", { selected: game.id === selectedGame?.id })}
								onClick={() => {
									setSelectedGame(game);
									onGameSelect(game.id);
								}}
							>
								<div className="idCategory-container">
									{game.category && <div className="category-container">{game.category}</div>}
									<div className="gameId-container">
										{`#${game.id}`}
									</div>
								</div>
								<div className="clubName">{`${game.homeClubName} - ${game.awayClubName}`}</div>
								<div className="gameBottom">
									<div className="team-logo">
										<img src={game.homeClubLogo ?? clubPlaceholder} alt="logo" />
									</div>
									<div className="game-score">{game.homeTeamsScore ? `${game.homeTeamsScore}:${game.awayTeamsScore}` : "-"}</div>
									<div className="team-logo">
										<img src={game.awayClubLogo ?? clubPlaceholder} alt="logo" />
									</div>
									<div className="game-details">
										{`${game.homeTeamName ?? "n/a"} - ${game.awayTeamName ?? "n/a"}`}
										<span>{`${_any(`enum.gametype.${game.type}`)} ${game.date}`} </span>
									</div>
								</div>
							</div>
						);
					})}
				</div>
			</div>
		</div>
	);
}
